import { useOktaAuth } from '@okta/okta-react'
import { FC, useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import useSingleApplication from '../../../../../hooks/query/useSingleApplication'
import { useNewConnAllEnrollmentFormStore } from '../../../../../stores/projects/projectResource/ca/newConnAlEnrollmentFormStore'
import ConnectivityAnalyzerConfigureForm, {
	ConnectivityAnalyzerConfigureFormProps
} from '../../../../../components/ProjectResource/ConnectivityAnalyzerConfigureForm'
import useProjectResources from '../../../../../hooks/query/useProjectResources'
import LoadingSpinner from '../../../../../components/LoadingSpinner'
import {
	ProjectResourceRequest,
	ProjectResourceApproval
} from '../../../../../types/projectResource'
import useCreateNewProjectResource from '../../../../../hooks/mutate/useCreateNewProjectResource'
import CONSTANTS from '../../../../../constants'
import { useQueryClient } from '@tanstack/react-query'
import { useFlashbarStateStore } from '../../../../../stores/notifications/flashbarStateStore'

const ConnectivityAnalyzerConfigureGCP: FC = () => {
	const { id: applicationID } = useParams() as { id: string }
	const { authState } = useOktaAuth()
	const thisUserEmail = authState?.idToken?.claims.email as string

	const navigate = useNavigate()
	const location = useLocation()

	const {
		formValues,
		actions: { setFormValues, clearFormValues }
	} = useNewConnAllEnrollmentFormStore()

	const queryClient = useQueryClient()

	const {
		data: application,
		isLoading: isApplicationLoading,
		isError: isApplicationError
	} = useSingleApplication(applicationID)

	let projectResourcesApplicationID = encodeURIComponent(
		`GCPAPP#${applicationID}`
	)

	const { mutate: createProjectResource, isLoading } =
		useCreateNewProjectResource()

	const {addItem: addFlashbarItem, removeItem: removeFlashbarItem} = useFlashbarStateStore()

	const {
		data: resources,
		isLoading: isResourcesLoading,
		isError: isResourcesError
	} = useProjectResources(projectResourcesApplicationID, 'CNCT_ANLZR', true)

	const {
		control,
		trigger,
		setValue,
		setError,
	} = useForm({
		mode: 'onChange',
		reValidateMode: 'onBlur',
		defaultValues: formValues
	})

	useEffect(() => {
		setFormValues(
			'analysisFrequency',
			CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[0]
		)
		setValue('analysisFrequency', CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[0])

		setFormValues('viewerEmails', [])
    setValue('viewerEmails', [])

    console.log('FormValues useEffect#1', formValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (resources !== undefined && resources.length > 0) {
			switch (resources[0].details.analysisFrequency) {
				case 'daily':
					setFormValues(
						'analysisFrequency',
						CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[1]
					)
					setValue(
						'analysisFrequency',
						CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[1]
					)
					break
				case 'weekly':
					setFormValues(
						'analysisFrequency',
						CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[2]
					)
					setValue(
						'analysisFrequency',
						CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[2]
					)
					break
				case 'monthly':
					setFormValues(
						'analysisFrequency',
						CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[3]
					)
					setValue(
						'analysisFrequency',
						CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[3]
					)
					break
				case null:
					setFormValues(
						'analysisFrequency',
						CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[0]
					)
					setValue(
						'analysisFrequency',
						CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[0]
					)
					break
			}
		}
	}, [resources, setFormValues, setValue])

	if (isApplicationLoading || isResourcesLoading) return <LoadingSpinner />

	if (!application || !resources || isApplicationError || isResourcesError) {
		return <h2>An Error Has Occured</h2>
	}

	let approvalStatus = {} as ProjectResourceApproval[]

	if (application.tags !== undefined) {
		approvalStatus = application.tags.approvalStatus
	}

	const resetForm = () => {
    clearFormValues()
    queryClient.resetQueries({queryKey: ['projectResources']})
    navigate('../../', {relative: 'path'})
	}

	const submitForm = () => {
		trigger()
		console.log('FormValues in Parent', formValues)
		let newProjectResource: ProjectResourceRequest = {
			status: 'PENDING_APPROVAL',
			requestorID: thisUserEmail,
			details: {
				viewers: formValues.viewerEmails.map(
					(viewer: { label: string }) => viewer.label
				),
				projectID: projectResourcesApplicationID,
				projectName: application.details.applicationName,
				platformName: application.details.platformName.label,
				analysisFrequency: formValues.analysisFrequency
			},
			action: 'CREATE',
			resourceType: 'CNCT_ANLZR',
			approvalStatus: approvalStatus
		}

		console.log(newProjectResource)

		createProjectResource(newProjectResource, {
			onSuccess: () => {
				resetForm()
				addFlashbarItem({
					type: 'success',
					dismissible: true,
					dismissLabel: 'Dismiss success',
					onDismiss: () => removeFlashbarItem(`success:${location.pathname}`),
					id: `success:${location.pathname}`,
					content: `Sucessfully configured connectivity analyzer for ${application.details.platformName.label}/${application.details.applicationName}.`
				})
				addFlashbarItem({
					type: 'info',
					dismissible: true,
					dismissLabel: 'Dismiss info',
					onDismiss: () => removeFlashbarItem(`info:${location.pathname}`),
					id: `info:${location.pathname}`,
					header: `Information`,
					content: 'Recent changes made might take a few moments to be reflected.'
				})
			},
			onError: () => {
				resetForm()
				addFlashbarItem({
					type: 'error',
					dismissible: true,
					dismissLabel: 'Dismiss error',
					onDismiss: () => removeFlashbarItem(`error:${location.pathname}`),
					id: `error:${location.pathname}`,
					content: 'Failed to configure connectivity analyzer'
				})
			}
		})
	}

	let formProps: ConnectivityAnalyzerConfigureFormProps = {
		control: control,
		formValues: formValues,
		setFormValues: setFormValues,
		setError: setError,
		isLoading: isLoading,
		submitForm: submitForm,
		cancelForm: resetForm,
		platformName: application.details.platformName.label,
		projectName: application.details.applicationName
	}

	if (resources.length > 0) {
	  formProps.existingViewers = resources[0].details.viewers
    formProps.existingAnalysisFrequency = resources[0].details.analysisFrequency
	}
	return <ConnectivityAnalyzerConfigureForm {...formProps} />
}

export default ConnectivityAnalyzerConfigureGCP
